import { useToast } from '@kk/ui/components/Notifications/Toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { leasingApi } from '@/api';

export type CreateNewAgreementRequest = Parameters<
  typeof leasingApi.postApiV1NewaggrementCreateNewAggrement
>[0];

function normalizeAgreementId(response: string | undefined) {
  const agreementId = response?.trim().replace(/^"|"$/g, '') ?? '';

  if (!agreementId) {
    throw new Error('CreateNewAggrement did not return an agreement id.');
  }

  return agreementId;
}

export function useFrameworkShorts() {
  return useQuery({
    queryKey: ['frameworks', 'shorts'],
    queryFn: () => leasingApi.getApiV1FrameworksShorts(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['newAgreementCategories'],
    queryFn: () => leasingApi.getApiV1NewaggrementCategories(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useContactList(leasingCustomerId?: number | string) {
  const parsedLeasingCustomerId = Number(leasingCustomerId);
  const normalizedLeasingCustomerId =
    leasingCustomerId === undefined ||
    leasingCustomerId === '' ||
    !Number.isFinite(parsedLeasingCustomerId)
      ? -1
      : parsedLeasingCustomerId;

  return useQuery({
    queryKey: ['contactList', normalizedLeasingCustomerId],
    queryFn: () =>
      leasingApi.getApiV1NewaggrementContacts({
        leasingCustomerId: normalizedLeasingCustomerId,
      }),
  });
}

export function useCustomerList() {
  return useQuery({
    queryKey: ['customerList'],
    queryFn: () => leasingApi.getApiV1NewaggrementCustomers(),
  });
}

export function useCreateNewAgreement() {
  const { t } = useTranslation();
  const { showToast } = useToast();

  return useMutation<string, Error, CreateNewAgreementRequest>({
    mutationKey: ['new-agreement-create', 'create'],

    mutationFn: async (request) => {
      const response =
        await leasingApi.postApiV1NewaggrementCreateNewAggrement(request);

      return normalizeAgreementId(response);
    },
    onSuccess: () => {
      showToast({
        type: 'success',
        headline: t('common.newAgreementSave.onSuccess'),
        size: 'long',
        link: { href: 'toast' },
      });
    },
    onError: (error, request) => {
      console.error(error);
      console.warn('Upload failed', request);

      showToast({
        type: 'danger',
        headline: t('common.newAgreementSave.onError'),
        message: t('common.onError.message'),
        size: 'long',
        link: { href: 'toast' },
      });
    },
  });
}
